import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ExternalLink, Satellite } from 'lucide-react';

// Custom red location pin requested by user
const redMarkerIcon = L.icon({
  iconUrl: '/images/custom-location-pin.png',
  iconSize: [32, 46],
  iconAnchor: [16, 46],
  popupAnchor: [0, -46],
  className: 'custom-location-pin-img',
});

// Satellite (Sputnik) Layers - High resolution Esri World Imagery + Places overlay
const SPUTNIK_LAYERS = [
  {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri World Imagery',
    maxZoom: 19,
  },
  {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
    maxZoom: 19,
  },
];

export default function GoogleMapPreview({ office, className = '' }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);

  if (!office) return null;

  const lat = parseFloat(office.latitude) || 40.160331;
  const lng = parseFloat(office.longitude) || 67.827246;
  const googleMapsUrl = office.mapUrl || office.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  useEffect(() => {
    if (!mapContainerRef.current) return;

    if (!mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        center: [lat, lng],
        zoom: 15,
        minZoom: 5,
        maxZoom: 19,
        zoomControl: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        touchZoom: true,
        boxZoom: true,
        keyboard: true,
        dragging: true,
      });

      // Add Sputnik layers
      SPUTNIK_LAYERS.forEach((cfg) => {
        L.tileLayer(cfg.url, {
          attribution: cfg.attribution || '',
          maxZoom: cfg.maxZoom || 19,
        }).addTo(map);
      });

      const marker = L.marker([lat, lng], {
        icon: redMarkerIcon,
      }).addTo(map);

      const officeName = office.name || 'Jizzax Advokatlari';
      const officeAddress = office.addressUz || office.address || '';
      marker.bindPopup(`
        <div style="font-family: 'Poppins', sans-serif; padding: 4px 2px;">
          <strong style="color: #0f172a; font-size: 12px; display: block; margin-bottom: 3px;">${officeName}</strong>
          <span style="color: #475569; font-size: 11px; line-height: 1.3;">${officeAddress}</span>
        </div>
      `);

      mapInstanceRef.current = map;
      markerInstanceRef.current = marker;

      // Observe size changes to invalidate Leaflet canvas size reliably
      let resizeObserver = null;
      if (typeof ResizeObserver !== 'undefined' && mapContainerRef.current) {
        resizeObserver = new ResizeObserver(() => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.invalidateSize();
          }
        });
        resizeObserver.observe(mapContainerRef.current);
      }

      // Multiple delays to account for modal transitions and animations
      const timeouts = [50, 150, 300, 600, 1000].map((ms) =>
        setTimeout(() => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.invalidateSize();
          }
        }, ms)
      );

      return () => {
        timeouts.forEach((t) => clearTimeout(t));
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
          markerInstanceRef.current = null;
        }
      };
    } else {
      mapInstanceRef.current.setView([lat, lng], 15);
      if (markerInstanceRef.current) {
        markerInstanceRef.current.setLatLng([lat, lng]);
      }
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 100);
    }
  }, [lat, lng, office.id]);

  return (
    <div className={`bg-[#0a0f19]/95 rounded-2xl border border-[#1b2b46] overflow-hidden shadow-xl flex flex-col ${className}`}>
      {/* Map Leaflet Canvas Container: explicit relative height with absolute inset-0 avoids zero-height flex collapse */}
      <div className="relative w-full h-[400px] sm:h-[450px] bg-slate-950 overflow-hidden">
        <div
          ref={mapContainerRef}
          className="absolute inset-0 w-full h-full z-0"
          style={{ height: '100%', minHeight: '380px' }}
        />

        {/* Subtle Sputnik Badge (Top Right) */}
        <div className="absolute top-3 right-3 z-10 pointer-events-none">
          <span className="bg-[#070c14]/90 text-[#c5a880] text-[10px] font-bold px-2.5 py-1 rounded-full border border-[#c5a880]/30 shadow-md flex items-center gap-1.5 backdrop-blur-xs">
            <Satellite className="w-3 h-3 text-[#c5a880]" />
            <span>Sputnik</span>
          </span>
        </div>
      </div>

      {/* Symmetrical Bottom Coordinate & Link Bar */}
      <div className="bg-[#070c14] border-t border-[#1b2b46] px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400 shrink-0">
        <div className="flex items-center space-x-2">
          <span className="font-semibold text-slate-300">Koordinatalar:</span>
          <span className="font-mono text-xs text-[#c59b27] tracking-wide">
            {lat.toFixed(6)}, {lng.toFixed(6)}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#c59b27] hover:text-[#e0bb6b] font-medium transition-colors flex items-center gap-1 text-xs"
          >
            <span>Google xaritada ochish</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
